import { useState, useEffect } from 'react';
import { geocodeAddress } from '@shared/core/places/geocode';

/**
 * Result object returned by the geocoding hook.
 * Contains location coordinates, viewport bounds, formatted address, and place ID.
 */
interface GeocodeResult {
  /** Latitude and longitude coordinates */
  location: {
    lat: number;
    lng: number;
  };
  /** Unique Google Maps place ID */
  place_id: string;
}

/**
 * React hook for geocoding addresses using the backend geocoding service.
 * Provides loading state, error handling, and geocoding results.
 *
 * @param params - Optional initial address to geocode on mount
 * @returns Object containing geocode function, loading state, error state, and geocoding result
 *
 * @example
 * ```tsx
 * const { isLoading, error, result } = useGeocoding({ address: 'San Francisco' });
 * ```
 */

export const useGeocoding = ({ address, skip }: { address?: string; skip?: boolean } = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCalled, setIsCalled] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<GeocodeResult | null>(null);

  useEffect(() => {
    if (!address || skip) return;

    const geocode = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const geocodeResult = await geocodeAddress({ address });
        if (!geocodeResult) {
          setError('No results found');
          return;
        }

        setResult({
          location: geocodeResult.geometry.location,
          place_id: geocodeResult.place_id
        });
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
        setIsCalled(true);
      }
    };

    geocode();
  }, [address, skip]);

  return {
    isLoading,
    isCalled,
    error,
    result
  };
};
